import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/apiClient";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

const newItem = () => ({ key: `q_${Date.now().toString(36)}`, question_id: "", question_en: "", answer_id: "", answer_en: "" });

export default function AdminFaq() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/faq").then(r => setItems(r.data?.items || []));
  }, []);

  const upd = (i, patch) => setItems(prev => prev.map((it, k) => k === i ? { ...it, ...patch } : it));
  const rm = (i) => setItems(prev => prev.filter((_, k) => k !== i));
  const add = () => setItems(prev => [...prev, newItem()]);
  const move = (i, dir) => {
    setItems(prev => {
      const j = i + dir; if (j < 0 || j >= prev.length) return prev;
      const arr = [...prev]; [arr[i], arr[j]] = [arr[j], arr[i]]; return arr;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/faq", { items });
      setItems(data.items || []);
      toast.success(lang === "id" ? "FAQ tersimpan" : "FAQ saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
        <div>
          <h1 className="font-display text-3xl tracking-tight">{lang === "id" ? "FAQ Beranda" : "Homepage FAQ"}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {lang === "id" ? "Item FAQ ini tampil di seksi FAQ di halaman Beranda." : "These items render in the FAQ section on the homepage."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={add} variant="outline" className="rounded-full" data-testid="faq-add-btn"><Plus className="w-4 h-4 mr-1" /> {lang === "id" ? "Tambah" : "Add"}</Button>
          <Button onClick={save} disabled={saving} className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50" data-testid="faq-save-btn">
            {saving ? (lang === "id" ? "Menyimpan…" : "Saving…") : (lang === "id" ? "Simpan Semua" : "Save All")}
          </Button>
        </div>
      </div>

      <div className="max-w-4xl space-y-4" data-testid="faq-list">
        {items.map((it, i) => (
          <div key={i} className="p-5 rounded-2xl border border-border bg-card space-y-3" data-testid={`faq-row-${i}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground w-20">Key</Label>
              <Input value={it.key} onChange={(e) => upd(i, { key: e.target.value })} className="h-8 max-w-xs font-mono text-xs" data-testid={`faq-key-${i}`} />
              <div className="ml-auto flex items-center gap-1">
                <Button size="icon" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`faq-up-${i}`}><ArrowUp className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => move(i, 1)} disabled={i === items.length - 1} data-testid={`faq-down-${i}`}><ArrowDown className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => rm(i)} className="text-destructive" data-testid={`faq-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Pertanyaan (ID)" : "Question (ID)"}</Label>
                <Input value={it.question_id || ""} onChange={(e) => upd(i, { question_id: e.target.value })} data-testid={`faq-q-id-${i}`} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Pertanyaan (EN)" : "Question (EN)"}</Label>
                <Input value={it.question_en || ""} onChange={(e) => upd(i, { question_en: e.target.value })} data-testid={`faq-q-en-${i}`} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Jawaban (ID)" : "Answer (ID)"}</Label>
                <Textarea rows={3} value={it.answer_id || ""} onChange={(e) => upd(i, { answer_id: e.target.value })} data-testid={`faq-a-id-${i}`} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Jawaban (EN)" : "Answer (EN)"}</Label>
                <Textarea rows={3} value={it.answer_en || ""} onChange={(e) => upd(i, { answer_en: e.target.value })} data-testid={`faq-a-en-${i}`} />
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="p-8 text-center text-muted-foreground border border-dashed border-border rounded-2xl">
            {lang === "id" ? "Belum ada item. Klik Tambah untuk membuat FAQ pertama." : "No items yet. Click Add to create your first FAQ."}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
